import type { CatalogItem } from "../library/catalogQuery.js";
import { catalogCategories } from "../library/catalogQuery.js";
import { parseCatalogTitle } from "../home/catalogTitleMatch.js";
import { duplicateFlags } from "../library/duplicates.js";
import { isShelfCandidate, shelvesForCategory } from "../home/shelfFill.js";
import type { DiscoveryEntry, DiscoveryRelatedResult, DiscoveryRowsResult } from "../api/nativeCatalog.js";
import type { DiscoveryIndex, KindIndex, MetaTitle } from "./discoveryFiles.js";
import { blankTitle, pick, recommended, related, type PickTitle } from "./discoveryPick.js";

/**
 * Home's rows and a title page's Similar / You May Also Like for the WEBSITE (Section 26 step
 * 4): the same answers the APK gets natively from CatalogStore.discoveryRows and
 * discoveryRelated, worked out in the page over the catalog it already holds (useVodCatalog,
 * parental locks already applied) and the public discovery files. The matching follows
 * CatalogStore.availableTitles exactly:
 *
 * - by TMDB id: a catalog title the provider gave a TMDB id, that meta.json describes, and that
 *   can be shown on a shelf (shelfFill.ts isShelfCandidate);
 * - by title key: a title the provider gave NO TMDB id, whose normalised name is a meta.json
 *   title's, a series on the name alone, a film within one year (or with no year on file).
 * The first catalog title to claim a TMDB id keeps it.
 */

type Kind = "vod" | "series";

export interface Available {
  titles: Map<number, PickTitle>;
  /** TMDB id → the catalog title a tile opens. */
  itemOf: Map<number, CatalogItem>;
  /** Catalog item id → the item, for the related request's history and My List. */
  itemById: Map<string, CatalogItem>;
  /** TMDB id → the catalog title with that provider id (for the daily lists' titles meta.json lacks). */
  providerById: Map<number, CatalogItem>;
}

const kindOf = (index: DiscoveryIndex, kind: Kind): KindIndex => (kind === "series" ? index.series : index.vod);

const isKidsCategory = (id: string, media: "movie" | "tv"): boolean => shelvesForCategory(id, media, ["kids"]).length > 0;

/** The provider's Kids categories of a kind, by Home's own patterns (the Kids rule's third part). */
export function kidsCategoriesOf(items: readonly CatalogItem[], kind: Kind): Set<string> {
  const media = kind === "series" ? "tv" : "movie";
  return new Set(
    catalogCategories(items)
      .map(([id]) => id)
      .filter((id) => isKidsCategory(id, media)),
  );
}


/** meta.json's title with what this catalog knows about it: the provider's rating and Kids category. */
function describe(meta: MetaTitle, item: CatalogItem, kids: ReadonlySet<string>): PickTitle {
  return {
    ...meta,
    rating: item.rating ?? Number.NaN,
    kidsCategory: item.categoryId !== undefined && kids.has(item.categoryId),
  };
}

const hasProviderId = (item: CatalogItem): boolean => typeof item.tmdbId === "number" && item.tmdbId > 0;

/** Whether a catalog title with no TMDB id is the meta.json title: the year rule of availableByKey. */
function yearFits(kind: Kind, item: CatalogItem, meta: MetaTitle): boolean {
  if (kind === "series") return true;
  const year = "year" in item ? item.year : undefined;
  if (year === undefined || year === null) return true;
  return meta.metaYear !== null && Math.abs(year - meta.metaYear) <= 1;
}

export function matchCatalog(
  index: DiscoveryIndex,
  kind: Kind,
  items: readonly CatalogItem[],
  kids: ReadonlySet<string>,
): Available {
  const k = kindOf(index, kind);
  const out: Available = { titles: new Map(), itemOf: new Map(), itemById: new Map(), providerById: new Map() };
  // One entry per title (duplicates.ts): a copy that is not the best stream never claims a title,
  // but stays findable by its id (My List and history may hold that copy).
  const hidden = duplicateFlags(items).hiddenAll;
  for (const item of items) {
    out.itemById.set(item.id, item);
    if (hidden.has(item.id)) continue;
    if (!hasProviderId(item) || !isShelfCandidate(item)) continue;
    const id = item.tmdbId as number;
    if (!out.providerById.has(id)) out.providerById.set(id, item);
    const meta = k.byTmdb.get(id);
    if (!meta || out.titles.has(id)) continue;
    out.titles.set(id, describe(meta, item, kids));
    out.itemOf.set(id, item);
  }
  for (const item of items) {
    if (hasProviderId(item)) continue;
    const key = parseCatalogTitle(item.rawTitle).key;
    if (!key) continue;
    for (const meta of k.byKey.get(key) ?? []) {
      if (out.titles.has(meta.tmdb) || !yearFits(kind, item, meta)) continue;
      out.titles.set(meta.tmdb, describe(meta, item, kids));
      out.itemOf.set(meta.tmdb, item);
    }
  }
  return out;
}

/** The daily file's list: titles meta.json does not describe still count, by TMDB id, when the catalog has them. */
function rankedList(list: readonly number[], available: Available, kids: ReadonlySet<string>): number[] {
  const out: number[] = [];
  for (const id of list) {
    if (!available.titles.has(id)) {
      const item = available.providerById.get(id);
      if (!item) continue;
      const blank = blankTitle(id);
      blank.rating = item.rating ?? Number.NaN;
      blank.kidsCategory = item.categoryId !== undefined && kids.has(item.categoryId);
      available.titles.set(id, blank);
      available.itemOf.set(id, item);
    }
    out.push(id);
  }
  return out;
}

function entries(index: DiscoveryIndex, kind: Kind, available: Available, tmdbs: readonly number[]): DiscoveryEntry[] {
  const k = kindOf(index, kind);
  const out: DiscoveryEntry[] = [];
  for (const tmdb of tmdbs) {
    const item = available.itemOf.get(tmdb);
    if (!item) continue;
    const meta = k.byTmdb.get(tmdb);
    out.push({
      tmdb,
      ...(meta ? { label: meta.label } : {}),
      ...(meta && meta.metaYear !== null ? { year: meta.metaYear } : {}),
      item,
    });
  }
  return out;
}

export interface WebRowsRequest {
  kind: Kind;
  day: number;
  size: number;
  kids: boolean;
}

/** The day's rows, shaped as the device's discoveryRows answer. */
export function webDiscoveryRows(
  index: DiscoveryIndex,
  items: readonly CatalogItem[],
  request: WebRowsRequest,
  matched?: Available,
): DiscoveryRowsResult {
  const kids = kidsCategoriesOf(items, request.kind);
  const available = matched ?? matchCatalog(index, request.kind, items, kids);
  const k = kindOf(index, request.kind);
  // The daily lists may add titles to `available`, so the picker works on a copy of the map.
  const titles = copyOf(available);
  const trending = rankedList(k.trending, titles, kids);
  const popular = rankedList(k.popular, titles, kids);
  const picked = pick(request, titles.titles, trending, popular, index.names, request.kind !== "series");
  const rows: Record<string, DiscoveryEntry[]> = {};
  for (const [id, tmdbs] of picked.rows) rows[id] = entries(index, request.kind, titles, tmdbs);
  return {
    trendingYear: index.trendingYear,
    rows,
    spotlight: picked.spotlight
      ? {
          type: picked.spotlight.type,
          title: picked.spotlight.title,
          items: entries(index, request.kind, titles, picked.spotlight.tmdb),
        }
      : null,
  };
}

/** A working copy of a match, so the cached match is never changed by one request's lists. */
function copyOf(source: Available): Available {
  return {
    titles: new Map(source.titles),
    itemOf: new Map(source.itemOf),
    itemById: source.itemById,
    providerById: source.providerById,
  };
}

/** Catalog item ids → the discovery titles they are (by TMDB id, else title key; CatalogStore.discoveryIdsOf). */
function discoveryIdsOf(index: DiscoveryIndex, kind: Kind, available: Available, itemIds: readonly string[]): number[] {
  const k = kindOf(index, kind);
  const out: number[] = [];
  for (const itemId of itemIds) {
    const item = available.itemById.get(itemId);
    if (!item) continue;
    let tmdb: number | undefined;
    if (hasProviderId(item)) tmdb = item.tmdbId as number;
    else {
      const key = parseCatalogTitle(item.rawTitle).key;
      tmdb = key ? (k.byKey.get(key) ?? []).find((meta) => yearFits(kind, item, meta))?.tmdb : undefined;
    }
    if (tmdb !== undefined && !out.includes(tmdb)) out.push(tmdb);
  }
  return out;
}

export interface WebRelatedRequest {
  kind: Kind;
  itemId: string;
  size: number;
  kids: boolean;
  watchedIds: readonly string[];
  favouriteIds: readonly string[];
}

/** A title page's Similar and You May Also Like; null when the open title is not in meta.json. */
export function webDiscoveryRelated(
  index: DiscoveryIndex,
  items: readonly CatalogItem[],
  request: WebRelatedRequest,
  matched?: Available,
): DiscoveryRelatedResult | null {
  const kids = kidsCategoriesOf(items, request.kind);
  const available = matched ?? matchCatalog(index, request.kind, items, kids);
  const seedId = discoveryIdsOf(index, request.kind, available, [request.itemId])[0];
  const seed = seedId === undefined ? undefined : available.titles.get(seedId);
  if (!seed || !seed.described) return null;
  const watched = discoveryIdsOf(index, request.kind, available, request.watchedIds);
  const favourites = discoveryIdsOf(index, request.kind, available, request.favouriteIds);
  const taste = [...watched];
  for (const id of favourites) if (!taste.includes(id)) taste.push(id);
  const trending = kindOf(index, request.kind).trending;
  const found = related(seed, available.titles, taste, new Set(taste), trending, request.size, request.kids);
  return {
    similar: entries(index, request.kind, available, found.similar),
    youMayLike: entries(index, request.kind, available, found.youMayLike),
  };
}

export interface WebRecommendedRequest {
  kind: Kind;
  size: number;
  kids: boolean;
  /** The profile's history and My List, as catalog item ids (ids of the other side are skipped). */
  watchedIds: readonly string[];
  favouriteIds: readonly string[];
  /** How many titles the profile has in all, films and series together (shapes the taste share). */
  tasteTotal: number;
  /** localDayIndex: the row rotates daily. */
  day: number;
}

/** Home's "Recommended For You" for one side: the picks in order, as the device's answer has them. */
export function webDiscoveryRecommended(
  index: DiscoveryIndex,
  items: readonly CatalogItem[],
  request: WebRecommendedRequest,
  matched?: Available,
): DiscoveryEntry[] {
  const kids = kidsCategoriesOf(items, request.kind);
  const available = matched ?? matchCatalog(index, request.kind, items, kids);
  const taste = discoveryIdsOf(index, request.kind, available, request.watchedIds);
  for (const id of discoveryIdsOf(index, request.kind, available, request.favouriteIds)) if (!taste.includes(id)) taste.push(id);
  const picked = recommended(
    available.titles,
    taste,
    new Set(taste),
    kindOf(index, request.kind).trending,
    request.size,
    request.kids,
    request.tasteTotal,
    request.day,
  );
  return entries(index, request.kind, available, picked);
}

/** The open title's cast and directors or creators as Wikidata numbers, for cast photos. */
export function webDiscoveryPeople(
  index: DiscoveryIndex,
  items: readonly CatalogItem[],
  kind: Kind,
  itemId: string,
): { cast: number[]; people: number[] } | null {
  const available = matchCatalog(index, kind, items, new Set());
  const tmdb = discoveryIdsOf(index, kind, available, [itemId])[0];
  const meta = tmdb === undefined ? undefined : kindOf(index, kind).byTmdb.get(tmdb);
  return meta ? { cast: meta.cast, people: meta.people } : null;
}

/**
 * The catalog in "Most popular" order for the Movies and Series pages (owner, 2026-10-11): each
 * title's 90-day views from the discovery files, matched as above (by TMDB id, else by title
 * key), best first; a title the files do not know goes last, in the provider's order
 * (CatalogStore's "popular" sort does the same natively).
 */
export function orderByPopularity<T extends CatalogItem>(index: DiscoveryIndex, kind: Kind, items: readonly T[]): T[] {
  const k = kindOf(index, kind);
  const views = new Map<string, number>();
  for (const item of items) {
    let meta: MetaTitle | undefined;
    if (hasProviderId(item)) meta = k.byTmdb.get(item.tmdbId as number);
    else {
      const key = parseCatalogTitle(item.rawTitle).key;
      meta = key ? (k.byKey.get(key) ?? []).find((candidate) => yearFits(kind, item, candidate)) : undefined;
    }
    views.set(item.id, meta ? meta.views : -1);
  }
  // A stable sort: titles with the same views keep the provider's order.
  return [...items].sort((a, b) => (views.get(b.id) ?? -1) - (views.get(a.id) ?? -1));
}
